import { BorderRadiusCorners, Box, Text } from '@factorialco/deprecated-design-system'
import { PlannedBreakFragment } from 'generated/resources/graphql'

import { useTimeFormatted } from 'modules/core/hooks/useTimeFormatted'
import { dateBackendDateTimeToUIUtcTime } from 'modules/core/lib/dates'
import { formatToLowerCase } from 'modules/core/lib/formatTime'
import i18n from 'modules/core/lib/i18n'

import TooltipIcon from 'modules/shift_management/components/ShiftManagement/TooltipIcon'

interface Props {
  shiftId: number
  plannedBreaks: PlannedBreakFragment[]
  workAreaName: string
  notes: string
  locationName: string
  addressLine1: string
  city: string
}

const ShiftDetailsBlock = ({
  shiftId,
  plannedBreaks,
  workAreaName,
  notes,
  locationName,
  addressLine1,
  city,
}: Props) => {
  const formatTime = useTimeFormatted()

  const borderRadius: BorderRadiusCorners = {
    all: 'abs008',
  }

  const hasBreaks = !!plannedBreaks.length

  const shiftPlannedBreaks = plannedBreaks.filter((plannedBreak) => plannedBreak.shiftId == shiftId)

  const hasLocation = !!locationName && !!addressLine1 && !!city

  return (
    <>
      <Box
        flexDirection="row"
        border={{
          all: {
            width: 's2',
            style: 'solid',
            color: 'grey400',
          },
        }}
        borderRadius={borderRadius}
        padding="s16"
        width="full"
      >
        <Box
          flexDirection="column"
          gap="s24"
          alignItems="center"
          paddingBottom="s2"
          justifyContent="flexStart"
        >
          {hasBreaks && (
            <Box flexDirection="row" gap="s4" alignSelf="flexStart">
              <TooltipIcon.Small icon="coffee" label="Break" />
              <Box flexDirection="column" gap="s4">
                {shiftPlannedBreaks.map((plannedBreak) => (
                  <Box key={plannedBreak.id} alignSelf="baseline" alignItems="baseline">
                    <Text size="200" color="grey800" weight="semibold">
                      {plannedBreak.breakConfiguration?.name}
                    </Text>
                    <Text size="200" color="grey800" weight="normal">
                      {!!plannedBreak && plannedBreak?.startAt && plannedBreak?.endAt
                        ? i18n.t(
                            'shift_management.swap_shifts.approval_requests.inbox.breaks_duration_long',
                            {
                              duration: plannedBreak.duration,
                              from: formatToLowerCase(
                                formatTime(dateBackendDateTimeToUIUtcTime(plannedBreak.startAt))
                              ),
                              to: formatToLowerCase(
                                formatTime(dateBackendDateTimeToUIUtcTime(plannedBreak.endAt))
                              ),
                            }
                          )
                        : i18n.t(
                            'shift_management.swap_shifts.approval_requests.inbox.breaks_duration_short',
                            {
                              duration: plannedBreak.duration,
                            }
                          )}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {hasLocation && (
            <Box flexDirection="row" gap="s4" alignSelf="flexStart">
              <TooltipIcon.Small icon="pin" label={locationName} />
              <Box flexDirection="column" gap="s4">
                <Text size="200" color="grey800" weight="semibold">
                  {locationName}
                </Text>
                <Text size="200" color="grey800" weight="normal">
                  {addressLine1}, {city}
                </Text>
              </Box>
            </Box>
          )}

          {!!workAreaName && (
            <Box flexDirection="row" gap="s4" alignSelf="flexStart">
              <TooltipIcon.Small icon="space-control" label={workAreaName} />
              <Box flexDirection="column" gap="s4">
                <Text size="200" color="grey800" weight="semibold">
                  {workAreaName}
                </Text>
              </Box>
            </Box>
          )}

          {!!notes && (
            <Box flexDirection="row" gap="s4" alignSelf="flexStart">
              <TooltipIcon.Small icon="comment" label={notes} />
              <Box flexDirection="column" gap="s4">
                <Text size="200" color="grey800" weight="semibold">
                  {notes}
                </Text>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  )
}

export default ShiftDetailsBlock
